import { useEffect, lazy, useState, useCallback } from 'react';
import { Routes, Route, useLocation, matchPath, Navigate, useParams } from 'react-router-dom';
import { trackPageView, identifyUser } from './utils/analytics';
import { openCrispChat, setCrispLauncherVisible, syncCrispUser } from './utils/crisp';
import { useAuth0 } from '@auth0/auth0-react';
import { useTheme } from 'src/design-system';
import { setAuth0TokenGetter } from 'utils/authUtils';
import * as Sentry from '@sentry/react';

import Callback from 'src/Pages/Callback';
import UsersApi from 'api/users';
import ReferralsApi from 'api/referrals';
import Header from 'components/Header';
import { ImpersonationBanner } from 'components/admin/ImpersonationBanner';
import Loader from 'src/components/Loader/Loader';
import SideMenu from 'components/SideMenu';
import Footer from 'src/components/Footer';
import { ParentGuard } from 'components/ParentGuard';
import { CurrentSessionPlayerProvider } from 'src/contexts/CurrentSessionPlayerContext';
import { FavoritesProvider } from 'src/contexts/FavoritesContext';

import { Suspender } from 'src/Suspender';
import { RequiredFields } from 'components/RequiredFields';
import { AuthenticationGuard } from 'components/AuthGuard';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectUser } from 'store/user/selectors';
import { setAuthState } from 'src/store/user/userSlice';
import { useHasFeatureFlag } from 'src/hooks/featureFlags';
import { AUTHED_ROUTES } from 'src/Routes';
import { SUBSCRIPTION_ACTIVE_STATUSES } from 'api/types/user';
import { traceLog } from './utils/requestTrace';
import { clearPendingReferralCode, getPendingReferralCode } from 'src/utils/referrals';

import './App.css';

const AIAgentPanel = lazy(() => import('components/AIAgent/AIAgentPanel'));
const MobileAIAgent = lazy(() => import('components/AIAgent/MobileAIAgent'));
const Login = lazy(() => import('pages/Login'));
const Logout = lazy(() => import('pages/Logout'));
const AIPopup = lazy(() => import('pages/AIPopup'));
const CurrentSessionPlayer = lazy(() => import('components/CurrentSessionPlayer'));
const SharedSessionPage = lazy(() => import('src/Pages/SharedSession/SharedSessionPage'));
const SharedCampaignPage = lazy(() => import('src/Pages/SharedCampaign/SharedCampaignPage'));
const GoogleAuthCallback = lazy(() => import('src/Pages/GoogleAuthCallback'));
const DiscordScribeOAuthCallback = lazy(() => import('src/Pages/DiscordScribeOAuthCallback'));
const DiscordScribeLink = lazy(() => import('src/Pages/DiscordScribeLink'));
const CheckoutSuccess = lazy(() => import('src/Pages/CheckoutSuccess/CheckoutSuccess'));
const AccountLinking = lazy(() => import('src/Pages/AccountLinking'));
const AccountLinkingCallback = lazy(() => import('src/Pages/AccountLinkingCallback'));
const VerifyEmail = lazy(() => import('src/Pages/VerifyEmail'));
const NotFoundGate = lazy(() => import('src/Pages/NotFound/NotFoundGate'));
const InAppNotFoundPage = lazy(() => import('src/Pages/NotFound/InAppNotFoundPage'));
type AuthedRouteConfig = (typeof AUTHED_ROUTES)[keyof typeof AUTHED_ROUTES];

const getCurrentAuthedRoute = (pathname: string): AuthedRouteConfig | undefined =>
  Object.values(AUTHED_ROUTES).find((route) =>
    matchPath({ path: route.path, end: true }, pathname)
  );

const ShortSharedSessionRedirect = () => {
  const { hash } = useParams<{ hash: string }>();
  return <Navigate to={`/shared/${hash ?? ''}`} replace />;
};

const ShortSharedCampaignRedirect = () => {
  const { hash } = useParams<{ hash: string }>();
  return <Navigate to={`/shared-campaign/${hash ?? ''}`} replace />;
};

const RouteChangeTracker = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname);
    traceLog('app', 'route change', {
      path: location.pathname,
      search: location.search
    });
  }, [location.pathname, location.search]);

  return null;
};

const RoutePathnameEffect = ({ onPathnameChange }: { onPathnameChange: () => void }) => {
  const location = useLocation();

  useEffect(() => {
    onPathnameChange();
  }, [location.pathname, onPathnameChange]);

  return null;
};

const MobileAIAgentRail = ({
  hasScribeAI,
  isMobile
}: {
  hasScribeAI: boolean;
  isMobile: boolean;
}) => {
  const location = useLocation();
  const currentRoute = getCurrentAuthedRoute(location.pathname);

  // Only mount on mobile. Previously this was CSS-hidden (lg:hidden) but still
  // mounted on desktop, and its desktop counterpart was mounted on mobile -
  // two live AIAgentChat instances meant duplicate thread fetches and duplicate
  // "Scribe made a change" toasts (the mutation event carries no thread id to
  // dedupe on). Gate on isMobile so exactly one rail mounts per viewport.
  if (!hasScribeAI || !currentRoute?.aiEnabled || !isMobile) {
    return null;
  }

  return (
    <div className="lg:hidden">
      <Suspender>
        <MobileAIAgent />
      </Suspender>
    </div>
  );
};

const DesktopAIAgentRail = ({
  hasScribeAI,
  isMobile
}: {
  hasScribeAI: boolean;
  isMobile: boolean;
}) => {
  const location = useLocation();
  const currentRoute = getCurrentAuthedRoute(location.pathname);

  if (!hasScribeAI || !currentRoute?.aiEnabled || isMobile) {
    return null;
  }

  return (
    <div className="hidden h-full min-h-0 lg:block">
      <Suspender>
        <AIAgentPanel />
      </Suspender>
    </div>
  );
};

const AIPopupAuthedContent = () => (
  <Suspender>
    <CurrentSessionPlayerProvider>
      <div className="h-screen flex flex-col">
        <AIPopup />
        <CurrentSessionPlayer />
      </div>
    </CurrentSessionPlayerProvider>
  </Suspender>
);

const AccountLinkingCallbackAuthedContent = () => (
  <Suspender>
    <AccountLinkingCallback />
  </Suspender>
);

const AuthedRouter = () => {
  const { isAuthenticated } = useAuth0();
  const user = useAppSelector(selectUser);
  const isLoading = useAppSelector((state) => state.auth.isLoading);
  const crispToken = useAppSelector((state) => state.auth.crispToken);
  const dispatch = useAppDispatch();
  const hasScribeAI = useHasFeatureFlag('scribeAi');

  useEffect(() => {
    traceLog('authed-router', 'mounted');
    return () => {
      traceLog('authed-router', 'unmounted');
    };
  }, []);

  // Mobile navigation state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Check if we're on mobile
  const [isMobile, setIsMobile] = useState(false);

  const showCrisp = useCallback(() => {
    openCrispChat();
  }, []);

  // Handle responsive behavior
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    async function fetchAndSetUser() {
      try {
        const { data } = await UsersApi.fetchCurrentUser();
        const { data: flags } = await UsersApi.fetchFeatureFlags();

        dispatch(
          setAuthState({
            isAuthenticated: true,
            isLoading: false,
            user: data.user,
            featureFlags: flags,
            campaignLimits: data.campaignLimits,
            crispToken: data.crispToken
          })
        );

        identifyUser(data.user.id, {
          email: data.user.email,
          subscriptionTier: data.user.stripeSubscriptionStatus
        });

        if (import.meta.env.VITE_SENTRY_DSN) {
          Sentry.setUser({
            id: data?.user?.id,
            email: data?.user?.email,
            username: data?.user?.discordUsername,
            ip_address: '{{auto}}'
          });
        }

        const pendingReferralCode = getPendingReferralCode();
        if (pendingReferralCode) {
          try {
            const { data: attributionResult } =
              await ReferralsApi.attributeReferralCode(pendingReferralCode);
            if (
              attributionResult.status === 'accepted' ||
              attributionResult.status === 'already_attributed' ||
              attributionResult.status === 'rejected'
            ) {
              clearPendingReferralCode();
            }
          } catch (attributionError) {
            traceLog('referral', 'attribution failed; keeping pending code', {
              error: attributionError
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch user, redirecting to login:', error);
        Sentry.captureException(error, {
          tags: { context: 'fetchAndSetUser' }
        });
        window.location.assign('/login');
      }
    }

    if (isAuthenticated && !user) {
      fetchAndSetUser();
    } else if (!isAuthenticated && import.meta.env.VITE_SENTRY_DSN) {
      Sentry.setUser(null);
    }
  }, [dispatch, isAuthenticated, user]);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  // Handle mobile menu toggle
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isOnPricingPage =
    !user?.stripeSubscriptionStatus ||
    !SUBSCRIPTION_ACTIVE_STATUSES.includes(user?.stripeSubscriptionStatus);

  useEffect(() => {
    if (!isAuthenticated || !user || !crispToken) {
      return;
    }

    traceLog('crisp', 'sync');
    syncCrispUser(user, crispToken);
    // Hide the launcher on mobile except on pricing.
    // Mobile users can still open chat via the side menu "Chat with us" item.
    setCrispLauncherVisible(!(isMobile && !isOnPricingPage));
  }, [isAuthenticated, user, crispToken, isMobile, isOnPricingPage]);

  return (
    <ParentGuard>
      <CurrentSessionPlayerProvider>
        <FavoritesProvider>
          <RouteChangeTracker />
          <RoutePathnameEffect onPathnameChange={closeMobileMenu} />
          <div className="flex flex-col h-screen overflow-x-clip">
            {isLoading ? (
              <Loader />
            ) : (
              <div className="relative flex min-h-0 min-w-0 flex-1 overflow-x-clip">
                {/* Mobile overlay */}
                {isMobileMenuOpen && (
                  <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
                    onClick={() => setIsMobileMenuOpen(false)}
                  />
                )}

                {/* SideMenu with responsive props */}
                <SideMenu
                  isOpen={isMobileMenuOpen}
                  onClose={() => setIsMobileMenuOpen(false)}
                  onShowCrisp={showCrisp}
                  isMobile={isMobile}
                  onCollapsedChange={setIsSidebarCollapsed}
                />

                {/* Main content with responsive padding */}
                <div
                  className={`flex min-h-0 min-w-0 max-h-screen flex-1 flex-col overflow-x-clip transition-all duration-300 ease-in-out ps-0 ${
                    isSidebarCollapsed ? 'lg:ps-14' : 'lg:ps-72'
                  }`}>
                  <ImpersonationBanner />
                  <div className="lg:hidden">
                    <Header onMobileMenuToggle={toggleMobileMenu} />
                  </div>
                  <div className="relative min-w-0 flex-1 overflow-x-hidden overflow-y-auto pt-8 px-4">
                    <Routes>
                      {Object.values(AUTHED_ROUTES).map((route) => (
                        <Route key={route.path} path={route.path} Component={route.component} />
                      ))}
                      <Route
                        path="*"
                        element={
                          <Suspender>
                            <InAppNotFoundPage />
                          </Suspender>
                        }
                      />
                    </Routes>

                    <MobileAIAgentRail hasScribeAI={hasScribeAI} isMobile={isMobile} />
                  </div>

                  <CurrentSessionPlayer />
                  <Footer />
                </div>

                <DesktopAIAgentRail hasScribeAI={hasScribeAI} isMobile={isMobile} />
              </div>
            )}
          </div>
        </FavoritesProvider>
      </CurrentSessionPlayerProvider>
    </ParentGuard>
  );
};

export default function App() {
  const { isLoading, getAccessTokenSilently } = useAuth0();
  useTheme('dark');

  // API requests can start in child effects before this component's effects run.
  setAuth0TokenGetter(getAccessTokenSilently);

  return isLoading ? (
    <Loader />
  ) : (
    <Routes>
      {/* DO NOT SUSPEND CALLBACK. CAUSES A RACE CONDITION WHERE THE TOKEN IS NOT AVAILABLE. */}
      <Route path="/callback" element={<Callback />} />
      <Route
        path="/login"
        element={
          <Suspender>
            <Login />
          </Suspender>
        }
      />
      <Route
        path="/logout"
        element={
          <Suspender>
            <Logout />
          </Suspender>
        }
      />
      {/* AI Popup Route - Bypasses main layout */}
      <Route path="/ai-popup" element={<AuthenticationGuard component={AIPopupAuthedContent} />} />
      <Route
        path="/not-found"
        element={
          <Suspender>
            <NotFoundGate authedContent={<AuthenticationGuard component={AuthedRouter} />} />
          </Suspender>
        }
      />
      <Route
        path="/checkout-success"
        element={
          <Suspender>
            <CheckoutSuccess />
          </Suspender>
        }
      />
      <Route
        path="/required-fields"
        element={
          <Suspender>
            <RequiredFields />
          </Suspender>
        }
      />
      {/* Preview short-link fallbacks: the preview server owns /s and /c and
          redirects to the canonical routes below; these handle the case where a
          link resolves to the SPA origin directly. */}
      <Route path="/s/:hash" element={<ShortSharedSessionRedirect />} />
      <Route path="/c/:hash" element={<ShortSharedCampaignRedirect />} />
      {/* Public shared session route - no authentication required */}
      <Route
        path="/shared/:hash"
        element={
          <Suspender>
            <SharedSessionPage />
          </Suspender>
        }
      />
      {/* Public shared campaign route - no authentication required */}
      <Route
        path="/shared-campaign/:hash"
        element={
          <Suspender>
            <SharedCampaignPage />
          </Suspender>
        }
      />
      {/* Google OAuth callback route */}
      <Route
        path="/google-auth/callback"
        element={
          <Suspender>
            <GoogleAuthCallback />
          </Suspender>
        }
      />
      <Route
        path="/discord-scribe/oauth/callback"
        element={
          <AuthenticationGuard
            component={() => (
              <Suspender>
                <DiscordScribeOAuthCallback />
              </Suspender>
            )}
          />
        }
      />
      <Route
        path="/discord-scribe/link"
        element={
          <AuthenticationGuard
            component={() => (
              <Suspender>
                <DiscordScribeLink />
              </Suspender>
            )}
          />
        }
      />
      {/* Account linking confirmation page - no authentication required */}
      <Route
        path="/account-linking"
        element={
          <Suspender>
            <AccountLinking />
          </Suspender>
        }
      />
      {/* Account linking callback - requires authentication (user initiated from settings) */}
      <Route
        path="/account-linking/callback"
        element={<AuthenticationGuard component={AccountLinkingCallbackAuthedContent} />}
      />
      {/* Email verification required page - no authentication required */}
      <Route
        path="/verify-email"
        element={
          <Suspender>
            <VerifyEmail />
          </Suspender>
        }
      />
      <Route path="*" element={<AuthenticationGuard component={AuthedRouter} />} />
    </Routes>
  );
}
